import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bug,
  CircleDashed,
  CircleDot,
  Cpu,
  FolderKanban,
  LifeBuoy,
  type LucideIcon,
  Megaphone,
  Minus,
  Sparkles,
  WandSparkles,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "../../api";
import { personLabel } from "../../lib/people";
import { useFactory } from "../../store";
import type { IssuesMeta, IssueView, SlaStatus, UserBadgeInfo, WaitingOn } from "../../types";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export function useIssuesMeta() {
  return useQuery<IssuesMeta>({
    queryKey: ["issues-meta"],
    queryFn: api.issuesMeta,
    staleTime: 60_000,
  });
}

export const TYPE_ICONS: Record<string, LucideIcon> = {
  bug: Bug,
  enhancement: WandSparkles,
  feature: Sparkles,
  chore: Wrench,
  marketing: Megaphone,
  support: LifeBuoy,
  project: FolderKanban,
  technical: Cpu,
};

export function TypeIcon({
  typeKey,
  color,
  className,
}: {
  typeKey: string;
  color?: string;
  className?: string;
}) {
  const Icon = TYPE_ICONS[typeKey] ?? CircleDot;
  return <Icon className={cn("size-3.5", className)} style={color ? { color } : undefined} />;
}

/** Linear-style status disc: category decides the ring, colour comes from settings. */
export function StatusDisc({
  category,
  color,
  className,
}: {
  category?: string;
  color?: string;
  className?: string;
}) {
  if (category === "backlog") {
    return <CircleDashed className={cn("size-3.5", className)} style={{ color: color ?? "#8C9496" }} />;
  }
  return (
    <svg viewBox="0 0 16 16" className={cn("size-3.5", className)} aria-hidden>
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke={color ?? "#8C9496"}
        strokeWidth="1.8"
        strokeDasharray={category === "unstarted" ? "2.5 2" : undefined}
      />
      {category === "started" && <circle cx="8" cy="8" r="3" fill={color ?? "#8C9496"} opacity={0.75} />}
      {(category === "completed" || category === "cancelled") && (
        <circle cx="8" cy="8" r="4.5" fill={color ?? "#8C9496"} />
      )}
      {category === "completed" && (
        <path d="M5.5 8.2l1.8 1.8 3.2-3.8" stroke="white" strokeWidth="1.5" fill="none" />
      )}
      {category === "cancelled" && <path d="M6 6l4 4M10 6l-4 4" stroke="white" strokeWidth="1.4" />}
    </svg>
  );
}

export function PriorityIcon({ priority, className }: { priority: number; className?: string }) {
  if (priority === 1) return <AlertTriangle className={cn("size-3.5 text-orange-500", className)} />;
  if (priority === 0) return <Minus className={cn("size-3.5 text-muted-foreground/60", className)} />;
  const bars = priority === 2 ? 3 : priority === 3 ? 2 : 1;
  return (
    <svg viewBox="0 0 16 16" className={cn("size-3.5", className)} aria-hidden>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={1.5 + i * 5}
          y={11 - i * 4}
          width="3"
          height={4 + i * 4}
          rx="1"
          className={i < bars ? "fill-foreground/80" : "fill-muted-foreground/25"}
        />
      ))}
    </svg>
  );
}

export const PRIORITY_LABELS = ["No priority", "Urgent", "High", "Medium", "Low"];

const SLA_TONES: Record<SlaStatus, string> = {
  none: "",
  on_track: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  at_risk: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  breached: "bg-red-500/15 text-red-600 dark:text-red-400",
  met: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
};

export const SLA_LABELS: Record<SlaStatus, string> = {
  none: "No SLA",
  on_track: "On track",
  at_risk: "At risk",
  breached: "Breached",
  met: "Met",
};

export function SlaPill({ issue, className }: { issue: IssueView; className?: string }) {
  if (!issue.sla || issue.slaStatus === "none") return null;
  const due = issue.slaDueAt ? new Date(issue.slaDueAt) : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap tabular-nums",
            SLA_TONES[issue.slaStatus],
            className,
          )}
        >
          {issue.sla} · {SLA_LABELS[issue.slaStatus]}
        </span>
      </TooltipTrigger>
      <TooltipContent>{due ? `Due ${due.toLocaleString()}` : SLA_LABELS[issue.slaStatus]}</TooltipContent>
    </Tooltip>
  );
}

/** Group headings for the Human required list, in `WAITING_ON` order. */
export const WAITING_LABELS: Record<WaitingOn, string> = {
  signoff: "Business sign-off",
  held: "Held at a station",
  input: "Agent asking",
  draft: "Draft pull request",
  review: "Tech review outside the factory",
  stalled: "Stalled",
};

const WAITING_SHORT: Record<WaitingOn, string> = {
  signoff: "Sign-off",
  held: "Held",
  input: "Agent asking",
  draft: "Draft PR",
  review: "Tech review",
  stalled: "Stalled",
};

const WAITING_HINTS: Record<WaitingOn, string> = {
  signoff: "Test it, then sign off or request changes.",
  held: "A station paused the work order. Resume it with rework or continue.",
  input: "The running agent asked a question. Answer it on the work order.",
  draft: "Mark the pull request ready for review so it can merge.",
  review: "Built outside the factory. Review the pull request.",
  stalled: "The work order ended before the issue did. Pick it up or send it to Skynet again.",
};

/** What a person needs to do next, on any row that waits on one. */
export function WaitingPill({ issue, className }: { issue: IssueView; className?: string }) {
  const { agentById } = useFactory();
  if (!issue.waitingOn) return null;
  const station = issue.currentAgentId ? agentById.get(issue.currentAgentId)?.name : undefined;
  const label = issue.waitingOn === "held" && station ? `Held at ${station}` : WAITING_SHORT[issue.waitingOn];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex shrink-0 items-center rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap text-amber-700 dark:text-amber-300",
            className,
          )}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{WAITING_HINTS[issue.waitingOn]}</TooltipContent>
    </Tooltip>
  );
}

export function UserChip({
  user,
  size = "sm",
  className,
}: {
  user: UserBadgeInfo | null;
  size?: "sm" | "md";
  className?: string;
}) {
  if (!user) return <span className="text-xs text-muted-foreground">—</span>;
  const name = personLabel(user);
  const letters = name
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Avatar className={cn(size === "sm" ? "size-5" : "size-6", className)}>
          <AvatarFallback
            className={cn("text-[10px] font-medium", user.kind === "system" && "bg-primary/15 text-primary")}
          >
            {user.kind === "system" ? "SK" : letters}
          </AvatarFallback>
        </Avatar>
      </TooltipTrigger>
      <TooltipContent>{user.email.includes("@") ? `${name} · ${user.email}` : name}</TooltipContent>
    </Tooltip>
  );
}

export function LabelChip({ name, color, className }: { name: string; color: string; className?: string }) {
  return (
    <Badge variant="outline" className={cn("gap-1.5 rounded-full font-normal", className)}>
      <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
      {name}
    </Badge>
  );
}

export function TeamChip({ name, color, className }: { name: string; color: string; className?: string }) {
  return (
    <Badge variant="outline" className={cn("gap-1.5 rounded-sm font-normal", className)}>
      <span className="size-2 rounded-sm" style={{ backgroundColor: color }} />
      {name}
    </Badge>
  );
}
